import React, { useState } from 'react';
import { submitFeedback, formatIssueFeedback } from '../../utils/feedbackService';
import './ReportIssueModal.css';

/**
 * Report Issue Modal Component
 * 
 * Small popup positioned at top-right for reporting issues/feedback
 * Categories include General + all available documents from tabs
 */
const ReportIssueModal = ({ 
  isOpen, 
  onClose,
  onOpen,
  emailID, 
  leadId, 
  jobId, 
  source, 
  availableTabs = [] 
}) => {
  const [category, setCategory] = useState('General Issue');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [isClosing, setIsClosing] = useState(false);

  // Generate category options - use tab names directly
  const categoryOptions = [
    'General Issue',
    'General Feedback',
    ...availableTabs
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      console.error('Please enter a description');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Convert category to snake_case for backend doctype
      const doctype = category;
      // Format payload using feedback service
      const payload = formatIssueFeedback({
        userId: emailID,
        issueText: description,
        doctype: doctype,
        context: {
          leadId: leadId || 'N/A',
          jobId: jobId,
          category: category,
          page: category
        },
        source: source || 'WEB'
      });

      // Submit feedback
      await submitFeedback(payload);
      
      setSubmitStatus('success');
      
      // Clear form and close after 2 seconds
      setTimeout(() => {
        setDescription('');
        setCategory('General Issue');
        setSubmitStatus(null);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('[REPORT_ISSUE] Submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsClosing(true);
    setTimeout(() => {
      setDescription('');
      setCategory('General Issue');
      setSubmitStatus(null);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  return (
    <div className={`report-issue-wrapper ${isOpen && !isClosing ? 'open' : ''}`}>
      {/* Pull-tab - always visible, attached to modal */}
      <div 
        className="report-issue-pull-tab"
        onClick={() => {
          if (isOpen) {
            handleCancel();
          } else {
            onOpen();
          }
        }}
      >
        <span style={{ transform: 'rotate(180deg)', display: 'inline-block' }}>Report Issue</span>
      </div>

      {/* Modal panel */}
      {(isOpen || isClosing) && (
        <div className="report-issue-modal">
      <div className="modal-header">
        <h3>Report Issue</h3>
        <button 
          className="close-button" 
          onClick={handleCancel}
          type="button"
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isSubmitting}
          >
            {categoryOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe the issue or provide feedback..."
            rows={6}
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="status-message success">
            ✓ Issue reported successfully!
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="status-message error">
            ✗ Failed to submit. Please try again.
          </div>
        )}

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-submit"
            disabled={isSubmitting || !description.trim()}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
        </div>
      )}
    </div>
  );
};

export default ReportIssueModal;
